import { lockBodyScroll } from "@/lib/body-scroll-lock";
import { Link, useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { isAdmin } from "@/lib/permissions";
import { navGroups, settingsItem, visibleNavGroups } from "@/lib/navigation";
import { useEffect, useRef, useState } from "react";

export function Sidebar() {
  const { sidebarOpen, toggleSidebar } = useApp();
  const { role } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [adminCollapsed, setAdminCollapsed] = useState(true);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);

  const showLabels = sidebarOpen || mobileOpen;
  const settingsPath = isAdmin(role) ? "/settings/organization" : "/settings/profile";

  const isActive = (path: string) => {
    if (path === "/dashboard" && location.pathname === "/") return true;
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const groups = visibleNavGroups(navGroups, role);

  // Keep the collapsible Administration group open while the user is inside it,
  // so the active item always remains visible (wayfinding).
  const adminActive = navGroups.some(
    (g) => g.collapsible && g.items.some((i) => isActive(i.path)),
  );
  const adminExpanded = !adminCollapsed || adminActive;

  const closeMobile = () => setMobileOpen(false);

  // Close the mobile drawer with Escape, lock body scroll while open, and move
  // focus in/out of the drawer so keyboard users don't lose their place.
  useEffect(() => {
    if (mobileOpen) {
      wasOpen.current = true;
      closeRef.current?.focus();
    } else if (wasOpen.current) {
      wasOpen.current = false;
      if (hamburgerRef.current?.getClientRects().length) hamburgerRef.current.focus();
    }
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const releaseScroll = lockBodyScroll();
    return () => {
      document.removeEventListener("keydown", onKey);
      releaseScroll();
    };
  }, [mobileOpen]);

  // CSS hides the mobile drawer at lg; its state and document lock must close too.
  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const closeOnDesktop = () => {
      if (desktop.matches) setMobileOpen(false);
    };
    closeOnDesktop();
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, []);

  // Browser navigation and links outside the drawer must also release its lock.
  useEffect(() => setMobileOpen(false), [location.pathname]);

  const navLinkClass = (active: boolean) =>
    `w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
      active ? "bg-emerald-50 text-emerald-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
    } ${!sidebarOpen ? "lg:justify-center lg:px-2" : ""}`;

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={closeMobile} aria-hidden="true" />
      )}

      {/* Mobile hamburger */}
      <button
        ref={hamburgerRef}
        onClick={() => setMobileOpen(true)}
        aria-label="Open navigation menu"
        aria-expanded={mobileOpen}
        aria-controls="app-sidebar"
        className="fixed top-4 left-4 z-40 lg:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-white border border-slate-200"
      >
        <i className="ri-menu-line text-lg text-slate-700" aria-hidden="true" />
      </button>

      {/* Sidebar */}
      <aside
        id="app-sidebar"
        className={`fixed top-0 left-0 h-full bg-white border-r border-slate-200 z-40 transition-all duration-300 flex flex-col
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          ${sidebarOpen ? "w-64" : "w-20"}
          lg:translate-x-0
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-slate-100">
          <Link
            to="/dashboard"
            onClick={closeMobile}
            aria-label="Go to dashboard"
            title="Dashboard"
            className={`flex items-center gap-3 rounded-lg px-1 py-1 -ml-1 hover:bg-slate-50 transition-colors ${!sidebarOpen ? "lg:justify-center" : ""}`}
          >
            <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center shrink-0" aria-hidden="true">
              <i className="ri-stack-line text-white text-sm" />
            </div>
            {(sidebarOpen || mobileOpen) && (
              <span className="font-semibold text-slate-900 text-sm tracking-tight whitespace-nowrap">
                Tradivo
              </span>
            )}
          </Link>
          <button
            onClick={toggleSidebar}
            aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
            className="ml-auto hidden lg:flex w-7 h-7 items-center justify-center rounded-md hover:bg-slate-100"
          >
            {sidebarOpen ? (
              <i className="ri-arrow-left-s-line text-slate-500" aria-hidden="true" />
            ) : (
              <i className="ri-arrow-right-s-line text-slate-500" aria-hidden="true" />
            )}
          </button>
          <button
            ref={closeRef}
            onClick={closeMobile}
            aria-label="Close navigation menu"
            className="ml-auto lg:hidden w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100"
          >
            <i className="ri-close-line text-slate-500" aria-hidden="true" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-3 scrollbar-hide" aria-label="Main navigation">
          {groups.map((group) => {
            const collapsible = !!group.collapsible;
            // In icon-only mode there is no header/toggle, so items always render.
            const expanded = !collapsible || !showLabels || adminExpanded;
            const groupId = `nav-group-${group.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
            return (
              <div key={group.label} className="mb-4 last:mb-0">
                {showLabels &&
                  (collapsible ? (
                    <button
                      type="button"
                      onClick={() => setAdminCollapsed((c) => !c)}
                      aria-expanded={adminExpanded}
                      aria-controls={groupId}
                      className="w-full flex items-center justify-between px-3 pb-1 cursor-pointer"
                    >
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                        {group.label}
                      </span>
                      <i
                        className={
                          adminExpanded
                            ? "ri-arrow-up-s-line text-slate-400 text-xs"
                            : "ri-arrow-down-s-line text-slate-400 text-xs"
                        }
                        aria-hidden="true"
                      />
                    </button>
                  ) : (
                    <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                      {group.label}
                    </p>
                  ))}
                <div id={groupId} className="space-y-0.5" hidden={!expanded}>
                  {group.items.map((item) => {
                    const active = isActive(item.path);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={closeMobile}
                        aria-current={active ? "page" : undefined}
                        className={navLinkClass(active)}
                      >
                        <div className="w-5 h-5 flex items-center justify-center shrink-0" aria-hidden="true">
                          <i className={`${item.icon} text-base ${active ? "text-emerald-600" : "text-slate-500"}`} />
                        </div>
                        {showLabels && <span className="whitespace-nowrap">{item.label}</span>}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Settings at bottom — always visible */}
        <div className="p-3 border-t border-slate-100">
          <Link
            to={settingsPath}
            onClick={closeMobile}
            aria-current={isActive(settingsPath) ? "page" : undefined}
            className={navLinkClass(isActive(settingsPath))}
          >
            <div className="w-5 h-5 flex items-center justify-center shrink-0" aria-hidden="true">
              <i
                className={`${settingsItem.icon} text-base ${isActive(settingsPath) ? "text-emerald-600" : "text-slate-500"}`}
              />
            </div>
            {showLabels && <span className="whitespace-nowrap">{settingsItem.label}</span>}
          </Link>
        </div>
      </aside>
    </>
  );
}